// Geocoding szolgáltatás címek keresésére és koordináták lekérésére

export interface GeocodingResult {
  name: string
  address: string
  lat: number
  lng: number
  type?: string
  country?: string
  city?: string
}

export interface ReverseGeocodingPlace {
  name: string
  address: string
  lat: number
  lng: number
  type?: string
  country?: string
  city?: string
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

/**
 * Nominatim (OpenStreetMap) alapú geocoding - INGYENES
 */
export async function searchAddressNominatim(query: string): Promise<GeocodingResult[]> {
  if (!query || query.length < 3) return []

  try {
    console.log('🌍 Nominatim search:', query)
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        'accept-language': 'hu,en'
      }),
      {
        headers: {
          'User-Agent': 'CarTrip-App/1.0'
        }
      }
    )

    console.log('🌍 Nominatim response status:', response.status)

    if (!response.ok) {
      console.error('❌ Nominatim error:', response.status, response.statusText)
      const text = await response.text()
      console.error('❌ Response body:', text)
      throw new Error('Geocoding failed')
    }

    const data = await response.json()
    console.log('✅ Nominatim results:', data.length)

    return data.map((item: any) => ({
      name: item.display_name.split(',')[0],
      address: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      type: item.type,
      country: item.address?.country,
      city: item.address?.city || item.address?.town || item.address?.village
    }))
  } catch (error) {
    console.error('❌ Nominatim geocoding error:', error)
    return []
  }
}

/**
 * Mapbox Geocoding API - JOBB MINŐSÉG (ha van token)
 */
export async function searchAddressMapbox(query: string): Promise<GeocodingResult[]> {
  if (!query || query.length < 3 || !MAPBOX_TOKEN) return []

  try {
    console.log('🗺️ Mapbox search:', query)
    console.log('🗺️ Token exists:', !!MAPBOX_TOKEN)
    
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
      new URLSearchParams({
        access_token: MAPBOX_TOKEN,
        language: 'hu,en',
        limit: '5',
        types: 'place,locality,address,poi'
      })
    )

    console.log('🗺️ Mapbox response status:', response.status)

    if (!response.ok) {
      console.error('❌ Mapbox error:', response.status, response.statusText)
      const text = await response.text()
      console.error('❌ Response body:', text)
      throw new Error('Mapbox geocoding failed')
    }

    const data = await response.json()
    console.log('✅ Mapbox results:', data.features?.length || 0)

    return data.features.map((feature: any) => {
      const [lng, lat] = feature.center
      return {
        name: feature.text,
        address: feature.place_name,
        lat,
        lng,
        type: feature.place_type?.[0],
        country: feature.context?.find((c: any) => c.id.startsWith('country'))?.text,
        city: feature.context?.find((c: any) => c.id.startsWith('place'))?.text
      }
    })
  } catch (error) {
    console.error('❌ Mapbox geocoding error:', error)
    return []
  }
}

/**
 * Automatikus geocoding választás (Mapbox ha van token, különben Nominatim)
 */
export async function searchAddress(query: string): Promise<GeocodingResult[]> {
  // Ha van Mapbox token, azt használjuk (jobb minőség)
  if (MAPBOX_TOKEN) {
    const results = await searchAddressMapbox(query)
    if (results.length > 0) return results
  }

  // Fallback: Nominatim (ingyenes)
  return searchAddressNominatim(query)
}

/**
 * Reverse geocoding - koordinátákból cím
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  const place = await reverseGeocodePlace(lat, lng)
  return place.address
}

export async function reverseGeocodePlace(lat: number, lng: number): Promise<ReverseGeocodingPlace> {
  try {
    if (MAPBOX_TOKEN) {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?` +
        new URLSearchParams({
          access_token: MAPBOX_TOKEN,
          language: 'hu,en',
          types: 'place,locality,address'
        })
      )

      if (response.ok) {
        const data = await response.json()
        const feature = data.features?.[0]

        if (feature) {
          const placeType = feature.place_type?.[0]
          const city = feature.context?.find((item: any) => item.id?.startsWith('place'))?.text || feature.text
          const country = feature.context?.find((item: any) => item.id?.startsWith('country'))?.text

          return {
            name: feature.text || feature.place_name?.split(',')[0] || 'Unknown location',
            address: feature.place_name || feature.text || 'Unknown location',
            lat,
            lng,
            type: placeType,
            country,
            city
          }
        }
      }
    }

    // Fallback: Nominatim
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?` +
      new URLSearchParams({
        lat: lat.toString(),
        lon: lng.toString(),
        format: 'json',
        addressdetails: '1',
        namedetails: '1',
        zoom: '12',
        'accept-language': 'hu,en'
      }),
      {
        headers: {
          'User-Agent': 'CarTrip-App/1.0'
        }
      }
    )

    if (response.ok) {
      const data = await response.json()
      const address = data.address || {}
      const settlementName = address.city
        || address.town
        || address.village
        || address.hamlet
        || address.suburb
        || address.municipality
        || address.county
        || data.namedetails?.name
        || data.display_name?.split(',')?.[0]
        || 'Unknown location'

      return {
        name: settlementName,
        address: data.display_name || settlementName,
        lat,
        lng,
        type: address.city ? 'city'
          : address.town ? 'town'
          : address.village ? 'village'
          : address.hamlet ? 'hamlet'
          : data.type,
        country: address.country,
        city: address.city || address.town || address.village || address.hamlet || settlementName
      }
    }

    return {
      name: 'Unknown location',
      address: 'Unknown location',
      lat,
      lng,
      type: 'unknown'
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error)
    return {
      name: 'Unknown location',
      address: 'Unknown location',
      lat,
      lng,
      type: 'unknown'
    }
  }
}

/**
 * Debounce helper for search input
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}
